import { useMemo, useState } from 'react'

function readOrder(storageKey: string): string[] {
  try {
    const saved: unknown = JSON.parse(localStorage.getItem(storageKey) || '[]')
    return Array.isArray(saved)
      ? [...new Set(saved.filter((value): value is string => typeof value === 'string' && value.length > 0))]
      : []
  } catch {
    return []
  }
}

export function useExchangeChartOrder(availableExchanges: string[], storageKey: string, controlled?: {
  order: string[]
  onChange: (order: string[]) => void
}) {
  const savedOrder = useMemo(() => readOrder(storageKey), [storageKey])
  const [preference, setPreference] = useState({ storageKey, order: savedOrder })
  const order = controlled?.order ?? (preference.storageKey === storageKey ? preference.order : savedOrder)
  // Keep unavailable exchanges in the preference so returning to a coin restores
  // their positions. Newly available exchanges follow the existing chart order.
  const fullOrder = useMemo(() => [...new Set([...order, ...availableExchanges])], [order, availableExchanges])
  const orderedExchanges = useMemo(() => {
    const available = new Set(availableExchanges)
    return fullOrder.filter((exchange) => available.has(exchange))
  }, [fullOrder, availableExchanges])

  const moveExchange = (exchange: string, targetExchange: string) => {
    if (exchange === targetExchange || !orderedExchanges.includes(exchange) || !orderedExchanges.includes(targetExchange)) return
    const next = [...fullOrder]
    const targetIndex = next.indexOf(targetExchange)
    next.splice(next.indexOf(exchange), 1)
    next.splice(targetIndex, 0, exchange)
    if (controlled) {
      controlled.onChange(next)
      return
    }
    setPreference({ storageKey, order: next })
    try {
      localStorage.setItem(storageKey, JSON.stringify(next))
    } catch {
      // Reordering still works when browser storage is unavailable.
    }
  }

  return { orderedExchanges, moveExchange }
}
